import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { Sales, Tasks, Spares } from "../lib/api";
import { fmtDate } from "../lib/auditFormat";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Package, AlertTriangle, CheckCircle2, ArrowRight, Calendar, XCircle, Clock,
  ChevronRight, ListTodo,
} from "lucide-react";
import { cn } from "../lib/utils";
import { buildDeliveryDateTime, getTimeRemaining, useTicker } from "../lib/time";
import ExportButton from "../components/ExportButton";
import { TransitHoldList } from "../components/TransitHoldList";
import { defaultFilters, buildApiParams } from "../lib/filters";

const today = () => new Date().toISOString().slice(0, 10);

const STATUS_TABS = [
  { value: "pending",       label: "Pending" },
  { value: "completed",     label: "Completed" },
  { value: "not_available", label: "Not Available" },
  { value: "transit_hold",  label: "Transit Hold" },
];

const STATUS_OPTIONS = [
  { value: "pending",       label: "Pending" },
  { value: "completed",     label: "Completed" },
  { value: "not_available", label: "Not Available" },
  { value: "transit_hold",  label: "Transit Hold" },
];

// ──────────────── KPI Card ────────────────
function KpiCard({ tone, Icon, label, value, subtext, active, onClick, testid }) {
  const tones = {
    blue:    { bg: "bg-blue-50/70",    ring: "ring-blue-200",    icon: "bg-blue-100 text-blue-700",    activeRing: "ring-blue-400" },
    emerald: { bg: "bg-emerald-50/70", ring: "ring-emerald-200", icon: "bg-emerald-100 text-emerald-700", activeRing: "ring-emerald-400" },
    amber:   { bg: "bg-amber-50/70",   ring: "ring-amber-200",   icon: "bg-amber-100 text-amber-700",   activeRing: "ring-amber-400" },
    rose:    { bg: "bg-rose-50/70",    ring: "ring-rose-200",    icon: "bg-rose-100 text-rose-700",     activeRing: "ring-rose-400" },
    slate:   { bg: "bg-slate-50/70",   ring: "ring-slate-200",   icon: "bg-slate-100 text-slate-700",   activeRing: "ring-slate-400" },
  }[tone] || {};
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={cn(
        "group flex items-center gap-3 rounded-xl px-4 py-3 text-left ring-1 transition-all hover:-translate-y-0.5 hover:shadow-md",
        tones.bg, tones.ring,
        active && `ring-2 ${tones.activeRing} shadow-sm`,
      )}>
      <span className={cn("flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full", tones.icon)}>
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-600">{label}</div>
        <div className="mt-0.5 font-display text-3xl font-bold leading-none tracking-tight text-slate-900">{value}</div>
        <div className="mt-1 text-[11px] text-slate-500">{subtext}</div>
      </div>
      <ChevronRight className="h-4 w-4 text-slate-400 transition-transform group-hover:translate-x-0.5" />
    </button>
  );
}

function TimerPill({ deliveryAt }) {
  if (!deliveryAt) return <span className="text-xs text-slate-400">—</span>;
  const t = getTimeRemaining(deliveryAt);
  const tones = {
    success: "bg-emerald-50 text-emerald-800 border-emerald-200",
    warning: "bg-amber-50 text-amber-800 border-amber-200",
    danger: "bg-rose-50 text-rose-800 border-rose-200",
    neutral: "bg-slate-100 text-slate-700 border-slate-200",
  };
  return (
    <span className={cn(
      "inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 font-mono-data text-xs font-semibold",
      tones[t.tone],
    )}>
      <Clock className="h-3 w-3" />
      {t.label}
    </span>
  );
}

function StatusPill({ task, sale }) {
  if (task.status === "completed") {
    const overall = sale.spares?.overall_status;
    if (overall === "not_available") {
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-semibold text-slate-700">
          <XCircle className="h-3 w-3" /> Not Available
        </span>
      );
    }
    if (overall === "partial") {
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800">
          Partial
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">
        <CheckCircle2 className="h-3 w-3" /> Completed
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800">
      <span className="h-1.5 w-1.5 rounded-full bg-amber-500" /> Pending
    </span>
  );
}

function ToolbarSelect({ value, onChange, placeholder, options, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-9 w-[150px] text-xs" data-testid={testid}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default function SparesQueue() {
  const { activeLocationIds, locations, activeUser, users } = useApp();
  useTicker(60_000);
  const locParam = useMemo(() => activeLocationIds.join(","), [activeLocationIds]);
  const locMap = Object.fromEntries(locations.map((l) => [l.id, l]));
  const userMap = Object.fromEntries((users || []).map((u) => [u.id, u]));

  const [stats, setStats] = useState({ pending: 0, completed_today: 0, not_available: 0, delayed: 0 });
  const [rows, setRows] = useState([]);
  const [transitCount, setTransitCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [locFilter, setLocFilter] = useState("all");
  const [filters] = useState(defaultFilters());
  const [search, setSearch] = useState("");

  // Memoised CSV-export params — avoids a fresh object on every render that
  // would otherwise re-trigger ExportButton effects.
  const exportParams = useMemo(() => ({
    type: "SPARES",
    status: statusFilter === "not_available" ? "completed" : statusFilter,
    location_ids: locFilter === "all" ? locParam : locFilter,
    ...buildApiParams(filters),
  }), [statusFilter, locFilter, locParam, filters]);
  const [assignedToMe, setAssignedToMe] = useState(false);
  const [delayedOnly, setDelayedOnly] = useState(false);
  const [todayOnly, setTodayOnly] = useState(false);

  useEffect(() => {
    (async () => setStats(await Spares.stats({ location_ids: locParam })))();
  }, [locParam, statusFilter]);

  // Transit-hold count for the tab badge
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const sales = await Sales.list({ transit_only: true, location_ids: locParam });
        if (!cancelled) setTransitCount(sales.length);
      } catch {
        if (!cancelled) setTransitCount(0);
      }
    })();
    return () => { cancelled = true; };
  }, [locParam, statusFilter]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (statusFilter === "transit_hold") { setRows([]); return; }
      const params = {
        type: "SPARES",
        status: statusFilter === "not_available" ? "completed" : statusFilter,
        location_ids: locFilter === "all" ? locParam : locFilter,
        ...buildApiParams(filters),
      };
      let tasks = await Tasks.list(params);
      if (cancelled) return;
      const saleIds = [...new Set(tasks.map((t) => t.sale_id))];
      const sales = saleIds.length ? await Sales.byIds(saleIds) : [];
      if (cancelled) return;
      const saleBy = Object.fromEntries(sales.map((s) => [s.id, s]));
      let merged = tasks.map((t) => ({ task: t, sale: saleBy[t.sale_id] })).filter((x) => x.sale);
      if (statusFilter === "not_available") {
        merged = merged.filter((x) => x.sale.spares?.overall_status === "not_available");
      }
      const td = today();
      if (todayOnly) {
        merged = merged.filter((x) =>
          statusFilter === "pending"
            ? (x.sale.delivery?.promised_date || "") === td
            : (x.task.completed_at || "").slice(0, 10) === td,
        );
      }
      if (assignedToMe && activeUser?.id) {
        merged = merged.filter((x) => x.task.assigned_user_id === activeUser.id);
      }
      if (delayedOnly) {
        merged = merged.filter((x) =>
          x.task.status === "pending" &&
          (x.sale.delivery?.promised_date || "") &&
          x.sale.delivery.promised_date < td,
        );
      }
      if (search) {
        const q = search.toLowerCase();
        merged = merged.filter((x) =>
          (x.sale.sr_number || "").toLowerCase().includes(q) ||
          (x.sale.customer?.name || "").toLowerCase().includes(q) ||
          (x.sale.customer?.phone || "").includes(q) ||
          (x.sale.vehicle?.vin || "").toLowerCase().includes(q),
        );
      }
      merged.sort((a, b) => {
        const aDel = a.task.status === "pending" && (a.sale.delivery?.promised_date || "") &&
          (a.sale.delivery?.promised_date || "") < td;
        const bDel = b.task.status === "pending" && (b.sale.delivery?.promised_date || "") &&
          (b.sale.delivery?.promised_date || "") < td;
        if (aDel !== bDel) return aDel ? -1 : 1;
        return (a.sale.delivery?.promised_date || "").localeCompare(b.sale.delivery?.promised_date || "");
      });
      setRows(merged);
    })();
    return () => { cancelled = true; };
  }, [statusFilter, locFilter, locParam, search, assignedToMe, activeUser,
      delayedOnly, todayOnly, filters]);

  const visibleLocs = locations.filter((l) => activeLocationIds.includes(l.id));
  const readOnly = activeUser?.role !== "Spares" && activeUser?.role !== "Admin";

  const resetFilters = () => {
    setStatusFilter("pending"); setLocFilter("all"); setSearch("");
    setAssignedToMe(false); setDelayedOnly(false); setTodayOnly(false);
  };

  return (
    <div className="space-y-3" data-testid="spares-queue-page">
      <div>
        <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500">Spares Team</div>
        <h1 className="mt-0.5 font-display text-2xl font-bold tracking-tight text-slate-900">Spares Workqueue</h1>
        <p className="mt-0.5 text-xs text-slate-500">
          Assign accessories or mark Not Available · upload accessory invoice · keep delivery on track.
        </p>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard tone="blue"    Icon={ListTodo}     label="Total Pending"   value={stats.pending}
          subtext="All pending tasks"
          active={statusFilter === "pending" && !delayedOnly && !todayOnly}
          onClick={() => { setStatusFilter("pending"); setDelayedOnly(false); setTodayOnly(false); }}
          testid="metric-spares-pending" />
        <KpiCard tone="emerald" Icon={CheckCircle2} label="Completed Today" value={stats.completed_today}
          subtext="Processed today"
          active={statusFilter === "completed" && todayOnly && !delayedOnly}
          onClick={() => { setStatusFilter("completed"); setTodayOnly(true); setDelayedOnly(false); }}
          testid="metric-spares-today" />
        <KpiCard tone="slate"   Icon={XCircle}      label="Not Available"   value={stats.not_available}
          subtext="Accessory unavailable"
          active={statusFilter === "not_available"}
          onClick={() => { setStatusFilter("not_available"); setDelayedOnly(false); }}
          testid="metric-spares-na" />
        <KpiCard tone="rose"    Icon={AlertTriangle} label="Delayed"        value={stats.delayed}
          subtext="SLA breached"
          active={delayedOnly}
          onClick={() => {
            if (delayedOnly) { setDelayedOnly(false); return; }
            setStatusFilter("pending"); setTodayOnly(false); setDelayedOnly(true);
          }}
          testid="metric-spares-delayed" />
      </div>

      {/* Single filter toolbar — text-only */}
      <Card className="border-slate-200 px-3 py-2">
        <div className="flex flex-wrap items-center gap-2">
          <Input
            placeholder="Search SR / Customer / Mobile / VIN / Regn / Ledger"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 min-w-[260px] flex-[3]"
            data-testid="spares-search"
          />
          <ToolbarSelect value={locFilter} onChange={setLocFilter}
            placeholder="All Locations"
            options={[
              { value: "all", label: "All Locations" },
              ...visibleLocs.map((l) => ({ value: l.id, label: `${l.code} — ${l.name}` })),
            ]}
            testid="spares-loc-filter" />
          <ToolbarSelect value={statusFilter} onChange={setStatusFilter}
            placeholder="Any Status"
            options={STATUS_OPTIONS}
            testid="spares-status-filter" />
          <label className="inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 whitespace-nowrap">
            <input type="checkbox" checked={assignedToMe}
              onChange={(e) => setAssignedToMe(e.target.checked)}
              data-testid="spares-assigned-to-me" />
            Assigned to me
          </label>
          <button
            type="button"
            onClick={resetFilters}
            className="inline-flex h-9 items-center rounded-md border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-600 hover:bg-slate-50"
            data-testid="spares-reset"
          >
            Reset
          </button>
          <div className="ml-auto">
            <ExportButton
              endpoint="tasks"
              params={exportParams}
              prefix={`Spares_${statusFilter}`}
            />
          </div>
        </div>
      </Card>

      {/* Status tabs */}
      <div className="border-b border-slate-200">
        <div className="flex flex-wrap items-center gap-1">
          {STATUS_TABS.map((t) => {
            const isActive = statusFilter === t.value;
            const showCount = t.value === "transit_hold" && transitCount > 0;
            return (
              <button key={t.value} type="button" onClick={() => setStatusFilter(t.value)}
                className={cn(
                  "relative -mb-px px-3.5 py-2 text-sm font-semibold transition-colors",
                  isActive ? "text-blue-600" : "text-slate-500 hover:text-slate-800",
                )}
                data-testid={`spares-tab-${t.value}`}
              >
                {t.label}{showCount ? ` (${transitCount})` : ""}
                {isActive && <span className="absolute inset-x-0 bottom-0 h-0.5 rounded-full bg-blue-600" />}
              </button>
            );
          })}
        </div>
      </div>

      {statusFilter === "transit_hold" ? (
        <TransitHoldList
          locParam={locFilter === "all" ? locParam : locFilter}
          locMap={locMap}
          userMap={userMap}
          canRelease={false}
          testidPrefix="spares"
          onCountChange={setTransitCount}
        />
      ) : (
      <Card className="border-slate-200 overflow-hidden">
        {rows.length === 0 ? (
          <div className="px-6 py-10 text-center text-sm text-slate-500">No tasks found.</div>
        ) : (
          <div>
            <table className="w-full table-fixed divide-y divide-border text-sm">
              <colgroup>
                <col style={{ width: "20%" }} />
                <col style={{ width: "15%" }} />
                <col style={{ width: "20%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "9%" }} />
                <col style={{ width: "8%" }} />
              </colgroup>
              <thead className="bg-slate-50">
                <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  <th className="px-3 py-2">Customer</th>
                  <th className="px-3 py-2">Vehicle / Location</th>
                  <th className="px-3 py-2">Accessories</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">DSE</th>
                  <th className="px-3 py-2">Delivery</th>
                  <th className="px-3 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-white">
                {rows.map(({ task, sale }) => {
                  const td = today();
                  const delayed = task.status === "pending" &&
                    sale.delivery?.promised_date && sale.delivery.promised_date < td;
                  const items = (sale.accessory_items || []).filter(
                    (a) => (a.price || 0) > 0 || !/free/i.test(a.name || ""),
                  );
                  const deliveryAt = buildDeliveryDateTime(sale.delivery?.promised_date, sale.delivery?.promised_time);
                  const dse = userMap[sale.dse_id];
                  const loc = locMap[sale.location_id];
                  return (
                    <tr key={task.id}
                      className={cn(
                        "h-[62px] transition-colors hover:bg-slate-50",
                        delayed && "bg-rose-50/40",
                      )}
                      data-testid={`spares-row-${task.id}`}
                    >
                      {/* Customer + SR (merged) */}
                      <td className="px-3 py-1.5">
                        <div className="truncate text-sm font-semibold text-slate-900 leading-tight">
                          {sale.customer?.name || "—"}
                        </div>
                        <div className="truncate font-mono-data text-[11px] text-slate-500 leading-tight">
                          {sale.customer?.phone || "—"}
                        </div>
                        <div className="truncate font-mono-data text-[10px] font-bold text-slate-700 leading-tight">
                          {sale.sr_number || "—"}
                        </div>
                      </td>
                      <td className="px-3 py-1.5">
                        <div className="truncate text-sm text-slate-900 leading-tight">
                          {sale.vehicle?.model || "—"}
                        </div>
                        <div className="truncate font-mono-data text-[11px] text-slate-500 leading-tight">
                          {loc ? `${loc.name} (${loc.code})` : "—"}
                        </div>
                      </td>
                      <td className="px-3 py-1.5">
                        {items.length === 0 ? (
                          <span className="text-xs text-slate-400">—</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {items.slice(0, 2).map((a, i) => (
                              <span key={`${a.name || "item"}-${i}`}
                                className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] text-slate-700">
                                {a.name}
                              </span>
                            ))}
                            {items.length > 2 && (
                              <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">
                                +{items.length - 2}
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-1.5">
                        <div className="flex flex-wrap items-center gap-1">
                          <StatusPill task={task} sale={sale} />
                          <TimerPill deliveryAt={deliveryAt} />
                        </div>
                      </td>
                      <td className="px-3 py-1.5">
                        <span className="truncate text-xs font-medium text-slate-800"
                              data-testid={`spares-dse-${task.id}`}>
                          {dse?.name || "—"}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 font-mono-data text-[11px] text-slate-700">
                        {fmtDate(sale.delivery?.promised_date) || "—"}
                      </td>
                      <td className="px-3 py-1.5 text-right">
                        <Link to={`/spares/${sale.id}`}
                          className="inline-flex items-center gap-0.5 rounded-md bg-primary/5 px-2 py-1 text-[11px] font-semibold text-primary hover:bg-primary/10"
                          data-testid={`spares-open-${task.id}`}>
                          {task.status === "completed" || readOnly ? "View" : "Update"}
                          <ArrowRight className="h-3 w-3" />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      )}
    </div>
  );
}
